import { useEffect, useState } from 'react';
import Client from '../../api/Client';

const VISITOR_ID_KEY = 'site_visitor_id';
let visitorRequest: Promise<number | null> | null = null;

async function registerVisitor(): Promise<number | null> {
  try {
    let visitorId = localStorage.getItem(VISITOR_ID_KEY);
    if (!visitorId || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(visitorId)) {
      visitorId = crypto.randomUUID();
      // Persist before the request so retries keep the same visitor number.
      localStorage.setItem(VISITOR_ID_KEY, visitorId);
    }

    const { err, res } = await Client.client_().registerSiteVisitor(visitorId);
    const number = res?.result?.visitor_number;
    if (!err && Number.isSafeInteger(number) && number > 0) return number;
  } catch {
    // The footer stays quiet if storage or the counter service is unavailable.
  }
  return null;
}

function ordinalSuffix(number: number): string {
  if (number % 100 >= 11 && number % 100 <= 13) return 'th';
  if (number % 10 === 1) return 'st';
  if (number % 10 === 2) return 'nd';
  if (number % 10 === 3) return 'rd';
  return 'th';
}

const WELCOME_LINES = [
  'Happy to have you!',
  'Welcome!',
  "Glad you're here!",
  'Stay a while!',
];

function welcomeLine(): string {
  const index = Math.floor(Math.random() * WELCOME_LINES.length);
  return WELCOME_LINES[index];
}

export default function VisitorCounter({ hidden = false }: { hidden?: boolean }) {
  const [visitorNumber, setVisitorNumber] = useState<number | null>(null);
  const [welcome] = useState(welcomeLine);

  useEffect(() => {
    let active = true;
    if (!visitorRequest) {
      // A browser lock keeps simultaneous first visits in separate tabs together.
      const request = navigator.locks
        ? navigator.locks.request(VISITOR_ID_KEY, registerVisitor)
        : registerVisitor();
      visitorRequest = request.catch(() => null).then((number) => {
        if (number === null) visitorRequest = null;
        return number;
      });
    }
    visitorRequest.then((number) => {
      if (active) setVisitorNumber(number);
    });
    return () => { active = false; };
  }, []);

  return (
    <footer hidden={hidden} className="px-4 pb-6 pt-3 text-center">
      <div className="flex min-h-7 items-center justify-center text-xs text-muted-foreground">
        {visitorNumber !== null && (
          <p>
            You're the {visitorNumber.toLocaleString('en-US')}{ordinalSuffix(visitorNumber)} visitor. {welcome}
          </p>
        )}
      </div>
    </footer>
  );
}
